import { FC, useCallback, useEffect, useState } from "react";
import { Modal } from "@/components/elements/Modal";
import { Stack, Tooltip, Typography } from "@mui/material";
import { UseVisibleReturn } from "@/hooks/useVisible";
import { Form } from "@/components/elements/Form";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useShareUrlSchema } from "@/features/case/schemas/shareUrlSchema";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { Button } from "@/components/elements/Button";
import { IconActionHTTP, IconContentFileCopy, IconVisibility, IconVisibilityOff } from "@/assets/icons";
import { useUpdateSharePassword } from "@/features/case/hooks/use-update-share-password";
import { useParams } from "react-router-dom";
import { toast } from "@/utils/toast";
import dayjs from "dayjs";
import { useDialog } from "@/hooks/useDialog";
import { black } from "@/theme/colors";
import { InputAdornment } from "@mui/material";
import { IconBox } from "@/components/elements/IconBox";
import { makeUuid } from "@/utils/pieces";
import { COPY_FAILED_MESSAGE } from "@/constants/common";

type TCaseShareEnterpriseURLModalProps = {
  modal: UseVisibleReturn;
  initialShareUrl?: string;
  initialPassword?: string;
  initialShareUrlExpiredAt?: string;
};

type TShareForm = {
  link?: string;
  password?: string;
};

/** Generate a random password (32 chars, alphanumeric) */
const generatePassword = (): string => {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  return Array.from(crypto.getRandomValues(new Uint8Array(32)))
    .map((v) => chars[v % chars.length])
    .join("");
};

/** Build full share URL from token for display/copy */
const buildShareUrl = (token: string): string => `${window.location.origin}/shared/${token}`;

export const CaseShareEnterpriseURLModal: FC<TCaseShareEnterpriseURLModalProps> = ({
  modal,
  initialShareUrl = "",
  initialPassword = "",
  initialShareUrlExpiredAt,
}) => {
  const { caseId } = useParams<{ caseId: string }>();
  const [copyTooltipOpen, setCopyTooltipOpen] = useState(false);
  const [shareUrlExpiredAt, setShareUrlExpiredAt] = useState<string | undefined>(initialShareUrlExpiredAt);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const { shareUrlSchema } = useShareUrlSchema();
  const updateSharePassword = useUpdateSharePassword();
  const confirmRegenerateDialog = useDialog();

  // BE stores only the token; build full URL for display
  const initialDisplayUrl = initialShareUrl ? buildShareUrl(initialShareUrl) : "";

  const methods = useForm<TShareForm>({
    defaultValues: {
      link: initialDisplayUrl,
      password: initialPassword,
    },
    resolver: yupResolver(shareUrlSchema),
    mode: "all",
    reValidateMode: "onBlur",
  });

  const { control, watch, setValue, reset } = methods;

  // Sync form when API data arrives after initial render
  useEffect(() => {
    reset({ link: initialShareUrl ? buildShareUrl(initialShareUrl) : "", password: initialPassword });
    setShareUrlExpiredAt(initialShareUrlExpiredAt);
  }, [initialShareUrl, initialPassword, initialShareUrlExpiredAt, reset]);

  const linkValue = watch("link");
  const passwordValue = watch("password");
  const hasShareUrl = !!linkValue;

  // Generate token + password, then auto-save to backend
  const generateAndSave = useCallback(async () => {
    const token = makeUuid();
    const newPassword = generatePassword();
    const newExpiredAt = dayjs().add(1, "month").toISOString();

    setValue("link", buildShareUrl(token));
    setValue("password", newPassword);
    setShareUrlExpiredAt(newExpiredAt);

    // Save only the token to backend
    if (caseId) {
      await updateSharePassword.mutateAsync({
        caseId,
        payload: { sharePassword: newPassword, shareUrl: token, shareUrlExpiredAt: newExpiredAt },
      });
    }
  }, [caseId, setValue, updateSharePassword]);

  const handleGenerateUrl = useCallback(async () => {
    if (hasShareUrl) {
      const confirmed = await confirmRegenerateDialog.show();
      if (!confirmed) return;
    }
    await generateAndSave();
  }, [hasShareUrl, confirmRegenerateDialog, generateAndSave]);

  const isUrlExpired = hasShareUrl && shareUrlExpiredAt && dayjs(shareUrlExpiredAt).isBefore(dayjs());

  const handleCopy = useCallback(async () => {
    try {
      const lines = [`共有URL：`, `${linkValue}`];
      const trimmedPwd = passwordValue?.trim();
      if (trimmedPwd) {
        lines.push(`閲覧パスワード：`, trimmedPwd);
      }
      await navigator.clipboard.writeText(lines.join("\n"));
      (document.activeElement as HTMLElement)?.blur();
      setCopyTooltipOpen(true);
      setTimeout(() => setCopyTooltipOpen(false), 2000);
    } catch {
      toast.error(COPY_FAILED_MESSAGE);
    }
  }, [linkValue, passwordValue, isUrlExpired]);

  return (
    <Modal isOpen={modal.visible} onClose={modal.close} width={800} title="企業への案件情報共有" enableFooter={false}>
      <Typography variant="cap12">企業に共有するURL、パスワードをこちらから発行できます。</Typography>
      <Form methods={methods} onSubmit={() => {}} schema={shareUrlSchema}>
        <Stack gap={2} mt={3}>
          <Stack direction="row" alignItems="end" spacing={3} justifyContent="space-between">
            <Stack flex={1}>
              <FormField control={control} name="link">
                <Input disabled placeholder="" sx={{ "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: black[30], color: black[30] } }} />
              </FormField>
            </Stack>
            <Button
              variant="secondary"
              startIcon={<IconActionHTTP />}
              onClick={handleGenerateUrl}
              isLoading={updateSharePassword.isPending}
              size="sm"
              sx={{ width: "116px" }}
            >
              {hasShareUrl ? "URL再発行" : "URL発行"}
            </Button>
          </Stack>
          {hasShareUrl && (
            <Typography variant="cap12" color={black[30]} mt={-2}>
              有効期限：{dayjs(shareUrlExpiredAt).format("YYYY/MM/DD")}まで
            </Typography>
          )}
          <FormField control={control} name="password">
            <Input
              disabled
              placeholder=""
              type={!passwordValue || passwordVisible ? "text" : "password"}
              sx={{
                "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: black[30], color: black[30] },
              }}
              endAdornment={
                passwordValue ? (
                  <InputAdornment position="end" sx={{ opacity: 1, pointerEvents: "auto" }}>
                    <IconBox onClick={() => setPasswordVisible((v) => !v)} sx={{ p: 0, cursor: "pointer" }}>
                      {passwordVisible ? <IconVisibilityOff width={15} height={15} /> : <IconVisibility width={15} height={15} />}
                    </IconBox>
                  </InputAdornment>
                ) : undefined
              }
            />
          </FormField>
        </Stack>
      </Form>
      <Stack direction="row" mt={3} alignItems="center" justifyContent="space-between">
        <Button variant="ghost" onClick={modal.close} size="sm" sx={{ width: "102px" }}>
          キャンセル
        </Button>
        <Stack alignItems="flex-end">
          <Tooltip title="コピーしました" open={copyTooltipOpen} arrow placement="top">
            <span>
              <Button
                variant="secondary"
                size="sm"
                sx={{ width: "172px" }}
                startIcon={<IconContentFileCopy />}
                onClick={handleCopy}
                disabled={!hasShareUrl}
              >
                共有情報をコピー
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>
      {isUrlExpired && (
        <Stack direction="row" justifyContent="flex-end">
          <Typography variant="cap12" sx={{ color: "#EC7126", mt: 1 }}>
            共有URLの有効期限が切れています。URL再発行してください。
          </Typography>
        </Stack>
      )}
      <Modal
        isOpen={confirmRegenerateDialog.open}
        onClose={confirmRegenerateDialog.cancel}
        onOk={confirmRegenerateDialog.confirm}
        labelClose="いいえ"
        labelOk="はい"
        width={500}
        title="URL再発行の確認"
      >
        <Stack mb={2}>
          <Typography>すでに発行されたURLは無効になりますがよろしいですか？</Typography>
        </Stack>
      </Modal>
    </Modal>
  );
};
